'use client'

/* eslint-disable react-hooks/rules-of-hooks -- Codex browser mode must return before Clerk hooks run without ClerkProvider. */

import {createContext, createElement, type ComponentProps, useCallback, useContext, useEffect, useRef, useSyncExternalStore} from 'react'
import {ClerkOfflineError} from '@clerk/nextjs/errors'
import {
    ClerkProvider,
    RedirectToTasks as ClerkRedirectToTasks,
    TaskChooseOrganization as ClerkTaskChooseOrganization,
    TaskResetPassword as ClerkTaskResetPassword,
    TaskSetupMFA as ClerkTaskSetupMFA,
    getToken as getClerkToken,
    useAuth as useClerkAuth,
    useClerk as useClerkRuntime,
    useSession as useClerkSession,
    useSignIn as useClerkSignIn,
    useSignUp as useClerkSignUp,
    useUser as useClerkUser,
} from '@clerk/nextjs'
import {
    getCodexBrowserToken,
    getCodexBrowserUserId,
    isCodexBrowserModeEnabled,
    isCodexBrowserSignedIn,
    signOutCodexBrowser,
} from '@/lib/codex-browser-mode'

export {ClerkProvider}

type ClerkAuthState = ReturnType<typeof useClerkAuth>
type ClerkGetToken = ClerkAuthState['getToken']
type ClerkGetTokenOptions = Parameters<ClerkGetToken>[0]
type ClerkGlobalGetTokenOptions = Parameters<typeof getClerkToken>[0]
type AuthLoadWaiter = (loaded: boolean) => void
type ClerkUserState = ReturnType<typeof useClerkUser>
type ClerkSessionState = ReturnType<typeof useClerkSession>
type ClerkRuntimeState = ReturnType<typeof useClerkRuntime>
type ClerkSignInState = ReturnType<typeof useClerkSignIn>
type ClerkSignUpState = ReturnType<typeof useClerkSignUp>

const getCodexBrowserAuthToken = async () => getCodexBrowserToken()
const subscribeCodexBrowserAuth = () => () => undefined
const getCodexBrowserServerSignedIn = () => process.env.NEXT_PUBLIC_FITRO_CODEX_BROWSER_AUTH_STATE !== 'anonymous'

function useCodexBrowserSignedIn(): boolean {
    return useSyncExternalStore(subscribeCodexBrowserAuth, isCodexBrowserSignedIn, getCodexBrowserServerSignedIn)
}

function codexAuthState(isSignedIn: boolean): ClerkAuthState {
    return {
        isLoaded: true,
        isSignedIn,
        userId: isSignedIn ? getCodexBrowserUserId() : null,
        getToken: getCodexBrowserAuthToken,
    } as unknown as ClerkAuthState
}

/** True when the request carried no Clerk client session, so the page can render signed out before Clerk loads. */
export const SignedOutRequestContext = createContext(false)

const SIGNED_OUT_AUTH = {
    isLoaded: true,
    isSignedIn: false,
    userId: null,
    sessionId: null,
    sessionClaims: null,
    actor: null,
    orgId: null,
    orgRole: null,
    orgSlug: null,
} as const

export function RedirectToTasks(props: ComponentProps<typeof ClerkRedirectToTasks>) {
    if (isCodexBrowserModeEnabled()) {
        return null
    }
    return createElement(ClerkRedirectToTasks, props)
}

export function TaskChooseOrganization(props: ComponentProps<typeof ClerkTaskChooseOrganization>) {
    if (isCodexBrowserModeEnabled()) {
        return null
    }
    return createElement(ClerkTaskChooseOrganization, props)
}

export function TaskResetPassword(props: ComponentProps<typeof ClerkTaskResetPassword>) {
    if (isCodexBrowserModeEnabled()) {
        return null
    }
    return createElement(ClerkTaskResetPassword, props)
}

export function TaskSetupMFA(props: ComponentProps<typeof ClerkTaskSetupMFA>) {
    if (isCodexBrowserModeEnabled()) {
        return null
    }
    return createElement(ClerkTaskSetupMFA, props)
}

export function useUser(): ClerkUserState {
    if (isCodexBrowserModeEnabled()) {
        const isSignedIn = useCodexBrowserSignedIn()
        if (!isSignedIn) {
            return {isLoaded: true, isSignedIn: false, user: null} as unknown as ClerkUserState
        }
        return {
            isLoaded: true,
            isSignedIn: true,
            user: {
                id: getCodexBrowserUserId(),
                fullName: 'Codex Customer',
                firstName: 'Codex',
                imageUrl: '',
                primaryEmailAddress: {
                    emailAddress: 'codex.customer@fitro.local',
                },
            },
        } as unknown as ClerkUserState
    }
    return useClerkUser()
}

export function useSession(): ClerkSessionState {
    if (isCodexBrowserModeEnabled()) {
        const isSignedIn = useCodexBrowserSignedIn()
        if (!isSignedIn) {
            return {isLoaded: true, isSignedIn: false, session: null} as unknown as ClerkSessionState
        }
        return {
            isLoaded: true,
            isSignedIn: true,
            session: {
                id: 'sess_codex_browser',
                status: 'active',
                currentTask: null,
            },
        } as unknown as ClerkSessionState
    }
    return useClerkSession()
}

export function useClerk(): ClerkRuntimeState {
    if (isCodexBrowserModeEnabled()) {
        return {
            signOut: async (options?: {redirectUrl?: string}) => signOutCodexBrowser(options?.redirectUrl),
        } as unknown as ClerkRuntimeState
    }
    return useClerkRuntime()
}

export function useSignIn(): ClerkSignInState {
    if (isCodexBrowserModeEnabled()) {
        return {isLoaded: true, signIn: null, setActive: async () => undefined} as unknown as ClerkSignInState
    }
    return useClerkSignIn()
}

export function useSignUp(): ClerkSignUpState {
    if (isCodexBrowserModeEnabled()) {
        return {isLoaded: true, signUp: null, setActive: async () => undefined} as unknown as ClerkSignUpState
    }
    return useClerkSignUp()
}

export async function getToken(options?: ClerkGlobalGetTokenOptions) {
    if (isCodexBrowserModeEnabled()) {
        return getCodexBrowserToken()
    }
    return getClerkToken(options)
}

export function useAuth() {
    if (isCodexBrowserModeEnabled()) {
        return codexAuthState(useCodexBrowserSignedIn())
    }

    const signedOutRequest = useContext(SignedOutRequestContext)
    const auth = useClerkAuth()
    const authRef = useRef(auth)
    const authLoadWaitersRef = useRef<AuthLoadWaiter[]>([])

    authRef.current = auth

    useEffect(() => {
        if (!auth.isLoaded) return

        const waiters = authLoadWaitersRef.current
        authLoadWaitersRef.current = []
        for (const notifyLoaded of waiters) {
            notifyLoaded(true)
        }
    }, [auth.isLoaded])

    useEffect(() => {
        return () => {
            const waiters = authLoadWaitersRef.current
            authLoadWaitersRef.current = []
            for (const notifyLoaded of waiters) {
                notifyLoaded(false)
            }
        }
    }, [])

    const waitForLoadedAuth = useCallback(async (): Promise<boolean> => {
        if (typeof window === 'undefined') return false
        if (authRef.current.isLoaded) return true

        return await new Promise<boolean>((resolve) => {
            authLoadWaitersRef.current.push(resolve)
        })
    }, [])

    const getToken = useCallback(async (options?: ClerkGetTokenOptions) => {
        if (typeof window === 'undefined') {
            return null
        }

        if (!authRef.current.isLoaded) {
            const didLoad = await waitForLoadedAuth()
            if (!didLoad) {
                return null
            }
        }

        try {
            return await authRef.current.getToken(options)
        } catch (error) {
            if (error instanceof ClerkOfflineError) {
                return null
            }

            throw error
        }
    }, [waitForLoadedAuth])

    if (!auth.isLoaded && signedOutRequest) {
        return {...auth, ...SIGNED_OUT_AUTH, getToken} as ClerkAuthState
    }

    return {
        ...auth,
        getToken,
    }
}
